import { MutationFunction, QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { User } from '../../../types/User'
import { getApiClient } from '../api-client'

export interface JwtToken {
  jwt: string
}

export interface MerchantUsersStatsRequestDto {
  merchantIds: string[]
  createdOnDateLimit: Date
}

const SCOPE = 'user-profile'
const DETAIL = 'detail'
const MERCHANT_USERS = 'merchant-users'

const keysFactory = {
  detail: () => [{ scope: SCOPE, entity: DETAIL }] as const,
  merchantUsers: (merchantIds: string[], createdOnDateLimit: Date) =>
    [{ scope: SCOPE, entity: MERCHANT_USERS, merchantIds, createdOnDateLimit }] as const,
}

const getUserProfile = async (_ctx: QueryFunctionContext<ReturnType<(typeof keysFactory)['detail']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get('/MyProfile')

  return response.data as User
}

export function useGetUserProfile(userId: string | undefined, isFederatedUser: boolean): [User | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.detail(),
    queryFn: getUserProfile,
    enabled: !!userId && !isFederatedUser, // #62287 — skip fetching user profile for federated users since their profile is managed in Entra ID Iceberg, not B2C
  })

  return [data ?? null, isFetching]
}

const getMerchantUsersStats = async ({
  queryKey: [{ merchantIds, createdOnDateLimit }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantUsers']>>) => {
  const apiClient = getApiClient()
  const dto: MerchantUsersStatsRequestDto = { merchantIds, createdOnDateLimit }
  const response = await apiClient.post('/MyProfile/Users', dto)
  return response.data as User[]
}

export function useGetMerchantUsers(dto: MerchantUsersStatsRequestDto): [User[] | null, boolean, Error | null] {
  const { data, isLoading, error } = useQuery({
    queryKey: keysFactory.merchantUsers(dto.merchantIds, dto.createdOnDateLimit),
    queryFn: getMerchantUsersStats,
  })

  return [data ?? null, isLoading, error]
}

const updateUserProfile: MutationFunction<User, User> = async (dto: User) => {
  const apiClient = getApiClient()
  const response = await apiClient.put('/MyProfile', dto)
  return response.data as User
}

export function useUpdateUserProfile(): [MutationFunction<User, User>, boolean, boolean, () => void] {
  const queryClient = useQueryClient()
  const { isPending, isError, mutateAsync, reset } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(), data)
      return data
    },
  })
  return [mutateAsync, isPending, isError, reset]
}

export const manageUserProfile: MutationFunction<User, User> = async (dto: User) => {
  const apiClient = getApiClient()
  const response = await apiClient.put('/MyProfile/ManageProfile', dto)
  return response.data as User
}

export function useManageUserProfile(): [MutationFunction<User, User>, boolean, boolean, () => void] {
  const queryClient = useQueryClient()
  const { isPending, isError, mutateAsync, reset } = useMutation({
    mutationFn: manageUserProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(), data)
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}
